import posthogLib, { type PostHog, type PostHogConfig, type PostHogInterface } from 'posthog-js';
import { scrubText } from './datadog.utils';

let posthog: PostHog;

// Cross-domain attribution: the landing page (weave.figma.com) appends this
// param to outbound links so app signups can be tied back to a landing visit.
const LANDING_PAGE_UID_PARAM = 'weave_landing_page_uid';

const getQueryParam = (param: string): string | null => {
  if (typeof window === 'undefined') return null;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

// Capture the landing-page UID synchronously at module load, BEFORE React
// Router mounts. An unauthenticated landing on `/?weave_landing_page_uid=…` is
// immediately redirected to `/signin` via `<Navigate replace />`, which drops
// the query string — so reading it inside the async `loaded` callback can race
// the redirect and lose it. Reading here (this module is imported before
// `root.render`) guarantees we see the param; registration still waits for
// `loaded`.
const landingPageUid = getQueryParam(LANDING_PAGE_UID_PARAM);
if (landingPageUid && typeof window !== 'undefined') {
  // Strip the UID from the URL so it doesn't leak into shareable/bookmarked
  // links or get re-read on later navigation.
  const url = new URL(window.location.href);
  url.searchParams.delete(LANDING_PAGE_UID_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
}

// Persist the captured UID as a super property so it rides along on every
// subsequent event (pageviews, $identify, user_signed_up). register_once keeps
// the first-touch value and never overwrites it on later visits. Runs in
// posthog's `loaded` callback so the property is set before $identify /
// user_signed_up fire.
const captureLandingPageUid = (ph: PostHogInterface) => {
  if (!landingPageUid) return;
  ph.register_once({ [LANDING_PAGE_UID_PARAM]: landingPageUid });
};

const options = {
  ui_host: 'https://eu.posthog.com',
  api_host: 'https://posthog-proxy.weavy.ai',
  // This SDK has no disable_toolbar option. Reject its toolbar dependency before insertion.
  prepare_external_dependency_script: (script) =>
    new URL(script.src).pathname.endsWith('/toolbar.js') ? null : script,
  custom_campaign_params: ['ref'],
  loaded: captureLandingPageUid,
  // Page URLs can carry an OAuth code when the boot script did not run, an invite token, or an email.
  before_send: (event) => {
    for (const properties of [event?.properties, event?.$set, event?.$set_once]) {
      for (const [key, value] of Object.entries(properties ?? {})) {
        if (typeof value === 'string') properties![key] = scrubText(value);
      }
    }
    return event;
  },
  remote_config_refresh_interval_ms: 0,
  session_recording: {
    captureCanvas: {
      canvasFps: 1,
      canvasQuality: '0.1',
    },
  },
} satisfies Partial<PostHogConfig>;

export const shouldEnableAllFlags = () => {
  const envFlag = import.meta.env.VITE_ENABLE_ALL_FEATURE_FLAGS === 'true';
  const queryFlag = getQueryParam('enableAllFeatureFlags') === 'true';
  return envFlag || queryFlag;
};

export const shouldDisableAllFlags = () => {
  return getQueryParam('disableAllFeatureFlags') === 'true';
};

let flagsOverridden = false;

export const initPosthog = () => {
  if (!posthog) {
    posthog = posthogLib.init(import.meta.env.VITE_POSTHOG_KEY, options);

    const enableAll = shouldEnableAllFlags();
    const disableAll = shouldDisableAllFlags();

    // Override feature flags based on query params or env variables
    if (enableAll || disableAll) {
      posthog.onFeatureFlags(() => {
        if (flagsOverridden) return;
        flagsOverridden = true;

        const flagKeys = posthog.featureFlags.getFlags();
        const overriddenFlags = flagKeys.reduce<Record<string, boolean>>((acc, flag) => {
          acc[flag] = enableAll ? true : false;
          return acc;
        }, {});

        posthog.featureFlags.overrideFeatureFlags({ flags: overriddenFlags });
      });
    } else {
      // clear all overrides
      posthog.featureFlags.overrideFeatureFlags(false);
    }
  }
};

initPosthog();

export { posthog };
